import { useCallback } from 'react'
import type { UseMutationProps } from '@ztechapps/common.hook'
import { useActionMutation, useQueueTracking } from '@ztechapps/common.hook'
import { QueueTrackingKeyEnum } from '@ztechapps/common.contract'
import { gql } from '@apollo/client'

export const forceRecomputeAllProductQuantitiesQuery = gql`
  mutation forceRecomputeAllProductQuantities {
    forceRecomputeAllProductQuantities
  }
`

const defaultQuery = forceRecomputeAllProductQuantitiesQuery

/**
 * Hook that fires the recompute-all mutation and automatically tracks the
 * background job via `useQueueTracking`.  The returned `loading` is `true`
 * during both the mutation request AND the queue processing, so the consumer
 * gets a single unified busy state.
 */
export const useForceRecomputeAllProductQuantities = <TResult = unknown>(
  { query, ...props } = {} as UseMutationProps
) => {
  query = query ?? defaultQuery

  const [executeMutation, { error, loading: mutationLoading }] = useActionMutation<string>({ query, ...props })
  const { isRunning, startTracking, result, isChecking } = useQueueTracking<TResult>(
    QueueTrackingKeyEnum.ProductRecomputeAll
  )

  const execute = useCallback(
    async (args?: any) => {
      const jobId = await executeMutation(args)
      if (jobId) startTracking(jobId)
      return jobId
    },
    [executeMutation, startTracking]
  )

  const loading = mutationLoading || isRunning || isChecking

  return [execute, { error, loading, result }] as const
}
